/**
 * Enhanced Cart Popup with CRO Optimizations
 * Secure, performant, and conversion-focused
 */

(function() {
  'use strict';

  let stickyCart = null;
  let isVisible = false;
  let productData = null;
  let cachedElements = {};
  let scrollTicking = false;
  let isAddingToCart = false;

  function init() {
    try {
      cacheElements();
      if (!cachedElements.productForm) return;

      productData = extractProductData();
      if (!productData) return;

      createStickyCart();
      createMiniCartPopup();
      setupEventListeners();
      handleScroll();
    } catch (error) {
      console.error('Enhanced cart initialization failed:', error);
    }
  }

  function cacheElements() {
    cachedElements = {
      productForm: document.querySelector('.product-form, form[action*="cart/add"]'),
      productName: document.querySelector('.product-name h1, .product-title, h1'),
      priceEl: document.querySelector('.price-box .price, #price .price, .detail-price .price'),
      addToCartBtn: document.querySelector('.add-to-cart, .btn-cart, button[name="add"]')
    };
  }

  function extractProductData() {
    const { productName, priceEl, addToCartBtn } = cachedElements;
    
    if (!productName || !priceEl || !addToCartBtn) return null;

    return {
      name: productName.textContent.trim(),
      price: priceEl.textContent.trim(),
      available: !addToCartBtn.disabled && !addToCartBtn.classList.contains('disabled'),
      buttonText: addToCartBtn.querySelector('span')?.textContent.trim() || addToCartBtn.textContent.trim() || 'Add to Cart'
    };
  }

  function createStickyCart() {
    stickyCart = document.createElement('div');
    stickyCart.className = 'sticky-cart-enhanced';
    stickyCart.innerHTML = `
      <div class="sticky-cart-container">
        <div class="sticky-cart-content">
          <div class="sticky-cart-info">
            <div class="sticky-cart-price">${productData.price}</div>
            <div class="sticky-cart-name">${productData.name}</div>
          </div>
          <button class="sticky-cart-btn ${!productData.available ? 'disabled' : ''}" 
                  ${!productData.available ? 'disabled' : ''}>
            <i class="icon-cart"></i>
            <span>${productData.buttonText}</span>
          </button>
        </div>
      </div>
    `;

    addStyles();
    document.body.appendChild(stickyCart);
  }

  function createMiniCartPopup() {
    const popup = document.createElement('div');
    popup.id = 'mini-cart-popup';
    popup.innerHTML = `
      <div class="mini-cart-overlay"></div>
      <div class="mini-cart-content">
        <div class="mini-cart-header">
          <svg class="checkmark" viewBox="0 0 24 24">
            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
          </svg>
          <h3>Added to Cart!</h3>
        </div>
        <div class="mini-cart-product">
          <img class="product-img" src="" alt="">
          <div class="product-details">
            <h4 class="product-title"></h4>
            <p class="product-price"></p>
            <p class="product-variant"></p>
          </div>
        </div>
        <div class="mini-cart-actions">
          <button class="continue-shopping">Continue Shopping</button>
          <a href="/cart" class="view-cart">View Cart (<span class="cart-count">0</span>)</a>
        </div>
      </div>
    `;
    document.body.appendChild(popup);
    
    // Event listeners
    popup.querySelector('.continue-shopping').addEventListener('click', hideMiniCartPopup);
    popup.querySelector('.mini-cart-overlay').addEventListener('click', hideMiniCartPopup);
  }

  function setupEventListeners() {
    // Secure cart button click handler
    stickyCart.querySelector('.sticky-cart-btn').addEventListener('click', async function(e) {
      e.preventDefault();
      
      // Prevent multiple clicks
      if (this.disabled || this.classList.contains('loading') || isAddingToCart) {
        return;
      }
      
      const form = cachedElements.productForm;
      if (!form) return;
      
      // Set global flag to prevent multiple requests
      isAddingToCart = true;
      
      try {
        this.classList.add('loading');
        this.disabled = true;
        const span = this.querySelector('span');
        const originalText = span.textContent;
        span.textContent = 'Adding...';
        
        const formData = new FormData(form);
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: formData
        });
        
        if (!response.ok) {
          const errorData = await response.text();
          throw new Error(errorData || `HTTP ${response.status}`);
        }
        
        const data = await response.json();
        
        // Update cart UI first
        await updateCartUI();
        
        // Trigger immediate AJAX refresh
        if (window.AjaxCartUpdate) {
          window.AjaxCartUpdate.refreshAllCartElements();
        }
        
        // Show enhanced popup
        showMiniCartPopup(data);
        
        // Trigger conversion events
        triggerConversionEvents(data);
        
        // Show success message
        showSuccessMessage('Item added to cart!');
        
        // Force update cart counts immediately
        setTimeout(() => {
          fetch('/cart.js')
            .then(response => response.json())
            .then(cart => {
              document.querySelectorAll('.cart-count, .cart-qty, .cart-items').forEach(el => {
                el.textContent = cart.item_count;
              });
            })
            .catch(console.error);
        }, 500);
        
      } catch (error) {
        console.error('Add to cart failed:', error);
        showErrorMessage('Unable to add item. Please try again.');
      } finally {
        // Reset states
        this.classList.remove('loading');
        this.disabled = false;
        this.querySelector('span').textContent = originalText;
        
        // Reset global flag after delay to prevent rapid clicks
        setTimeout(() => {
          isAddingToCart = false;
        }, 1000);
      }
    });

    // Throttled scroll handler
    window.addEventListener('scroll', throttleScroll, { passive: true });

    // Handle variant changes
    const variantSelectors = document.querySelectorAll('.single-option-selector, input[name^="option"], .swatch input');
    variantSelectors.forEach(selector => {
      selector.addEventListener('change', updateProductInfo);
    });

    // Handle window resize
    window.addEventListener('resize', debounce(handleScroll, 100));
  }

  function throttleScroll() {
    if (!scrollTicking) {
      requestAnimationFrame(handleScroll);
      scrollTicking = true;
    }
  }

  function handleScroll() {
    scrollTicking = false;
    
    const isMobile = window.innerWidth <= 767;
    const { addToCartBtn } = cachedElements;
    const footer = document.querySelector('footer, .footer, .main-footer');
    
    if (!addToCartBtn) return;

    const btnRect = addToCartBtn.getBoundingClientRect();
    const footerRect = footer?.getBoundingClientRect();

    const shouldShow = isMobile || btnRect.top < -50;
    const shouldHide = footerRect && footerRect.top < window.innerHeight;
    const newVisibility = shouldShow && !shouldHide;

    if (newVisibility !== isVisible) {
      isVisible = newVisibility;
      stickyCart.classList.toggle('visible', isVisible);
    }
  }

  function showMiniCartPopup(productData) {
    const popup = document.getElementById('mini-cart-popup');
    if (!popup) {
      console.warn('Mini cart popup not found');
      return;
    }
    
    try {
      // Update popup content
      const img = popup.querySelector('.product-img');
      const title = popup.querySelector('.product-title');
      const price = popup.querySelector('.product-price');
      const variant = popup.querySelector('.product-variant');
      const count = popup.querySelector('.cart-count');
      
      if (img && productData.image) {
        img.src = productData.image.replace(/\.(jpg|png|webp)/, '_150x.$1');
        img.alt = productData.product_title || productData.title;
      }
      
      if (title) title.textContent = productData.product_title || productData.title;
      if (price) price.textContent = formatMoney(productData.price);
      
      if (variant) {
        if (productData.variant_title && productData.variant_title !== 'Default Title') {
          variant.textContent = productData.variant_title;
          variant.style.display = 'block';
        } else {
          variant.style.display = 'none';
        }
      }
      
      // Update cart count in popup
      fetch('/cart.js')
        .then(response => response.json())
        .then(cart => {
          if (count) count.textContent = cart.item_count;
        })
        .catch(error => console.error('Failed to update popup cart count:', error));
      
      // Show popup with animation
      popup.classList.add('show');
      
      // Auto hide after 4 seconds
      setTimeout(hideMiniCartPopup, 4000);
      
    } catch (error) {
      console.error('Error showing mini cart popup:', error);
    }
  }

  function hideMiniCartPopup() {
    const popup = document.getElementById('mini-cart-popup');
    if (popup) {
      popup.classList.remove('show');
    }
  }

  function updateCartUI() {
    return fetch('/cart.js')
      .then(response => {
        if (!response.ok) throw new Error('Failed to fetch cart');
        return response.json();
      })
      .then(cart => {
        // Update all cart count elements with more selectors
        const cartCounts = document.querySelectorAll('.cart-count, .cart-qty, .cart-items, .cart-total span:first-child, [data-cart-count]');
        cartCounts.forEach(el => {
          if (el) el.textContent = cart.item_count;
        });
        
        // Update cart prices
        const cartPrices = document.querySelectorAll('.cart-price .money, [data-cart-total]');
        cartPrices.forEach(el => {
          if (el) el.textContent = formatMoney(cart.total_price);
        });
        
        // Update mini cart popup count
        const popupCount = document.querySelector('#mini-cart-popup .cart-count');
        if (popupCount) popupCount.textContent = cart.item_count;
        
        // Trigger global cart update event
        window.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
        
        return cart;
      })
      .catch(error => {
        console.error('Cart update failed:', error);
        return null;
      });
  }

  function triggerConversionEvents(data) {
    // Google Analytics 4 Enhanced Ecommerce
    if (typeof gtag !== 'undefined') {
      gtag('event', 'add_to_cart', {
        currency: 'USD',
        value: data.price / 100,
        items: [{
          item_id: data.variant_id,
          item_name: data.product_title,
          category: data.product_type,
          quantity: data.quantity,
          price: data.price / 100
        }]
      });
    }

    // Facebook Pixel
    if (typeof fbq !== 'undefined') {
      fbq('track', 'AddToCart', {
        content_ids: [data.variant_id],
        content_type: 'product',
        value: data.price / 100,
        currency: 'USD'
      });
    }

    // Custom event for other tracking
    document.dispatchEvent(new CustomEvent('cart:item:added', { 
      detail: { 
        product: data,
        timestamp: Date.now()
      }
    }));
  }

  function updateProductInfo() {
    setTimeout(() => {
      const newProductData = extractProductData();
      if (!newProductData || !stickyCart) return;

      const priceEl = stickyCart.querySelector('.sticky-cart-price');
      const btnEl = stickyCart.querySelector('.sticky-cart-btn');
      const btnSpan = btnEl.querySelector('span');

      if (newProductData.price !== productData.price) {
        priceEl.classList.add('updating');
        setTimeout(() => {
          priceEl.textContent = newProductData.price;
          priceEl.classList.remove('updating');
        }, 150);
      }

      btnEl.disabled = !newProductData.available;
      btnEl.classList.toggle('disabled', !newProductData.available);
      btnSpan.textContent = newProductData.buttonText;

      productData = newProductData;
    }, 200);
  }

  function showErrorMessage(message) {
    showToast(message, 'error');
  }
  
  function showSuccessMessage(message) {
    showToast(message, 'success');
  }
  
  function showToast(message, type) {
    const toast = document.createElement('div');
    toast.className = `cart-toast cart-toast-${type}`;
    toast.textContent = message;
    toast.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      padding: 12px 20px;
      border-radius: 4px;
      color: white;
      font-weight: 500;
      z-index: 10001;
      transform: translateX(100%);
      transition: transform 0.3s ease;
      background: ${type === 'success' ? '#28a745' : '#dc3545'};
    `;
    
    document.body.appendChild(toast);
    
    setTimeout(() => {
      toast.style.transform = 'translateX(0)';
      setTimeout(() => {
        toast.style.transform = 'translateX(100%)';
        setTimeout(() => toast.remove(), 300);
      }, 2000);
    }, 100);
  }

  function formatMoney(cents) {
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(cents);
    }
    return (cents / 100).toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD'
    });
  }

  function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  function addStyles() {
    if (document.getElementById('enhanced-cart-styles')) return;

    const styles = document.createElement('style');
    styles.id = 'enhanced-cart-styles';
    styles.textContent = `
      .sticky-cart-enhanced {
        position: fixed;
        bottom: 0;
        left: 0;
        right: 0;
        background: rgba(255, 255, 255, 0.98);
        backdrop-filter: blur(10px);
        box-shadow: 0 -2px 20px rgba(0, 0, 0, 0.15);
        z-index: 1000;
        transform: translateY(100%);
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        opacity: 0;
        visibility: hidden;
      }

      .sticky-cart-enhanced.visible {
        transform: translateY(0);
        opacity: 1;
        visibility: visible;
      }

      .sticky-cart-container {
        max-width: 600px;
        margin: 0 auto;
        padding: 0 15px;
      }

      .sticky-cart-content {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 12px 0;
        gap: 15px;
      }

      .sticky-cart-info {
        flex: 1;
        min-width: 0;
      }

      .sticky-cart-price {
        font-size: 18px;
        font-weight: 700;
        color: #222529;
        line-height: 1.2;
        margin-bottom: 2px;
        transition: color 0.2s ease;
      }

      .sticky-cart-price.updating {
        color: #007bff;
      }

      .sticky-cart-name {
        font-size: 13px;
        color: #666;
        line-height: 1.3;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .sticky-cart-btn {
        display: flex;
        align-items: center;
        gap: 8px;
        background: linear-gradient(135deg, #222529, #333);
        color: #fff;
        border: none;
        padding: 12px 24px;
        border-radius: 6px;
        font-size: 14px;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        cursor: pointer;
        transition: all 0.2s ease;
        min-width: 140px;
        justify-content: center;
        position: relative;
        overflow: hidden;
      }

      .sticky-cart-btn:hover:not(.disabled) {
        background: linear-gradient(135deg, #333, #444);
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(34, 37, 41, 0.3);
      }

      .sticky-cart-btn.disabled {
        background: #ccc;
        cursor: not-allowed;
        opacity: 0.6;
      }

      .sticky-cart-btn.loading {
        opacity: 0.8;
        pointer-events: none;
      }

      .sticky-cart-btn.loading i {
        animation: spin 1s linear infinite;
      }

      @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }

      /* Mini Cart Popup */
      #mini-cart-popup {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        z-index: 10000;
        display: flex;
        align-items: center;
        justify-content: center;
        opacity: 0;
        visibility: hidden;
        transition: all 0.3s ease;
      }

      #mini-cart-popup.show {
        opacity: 1;
        visibility: visible;
      }

      .mini-cart-overlay {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.5);
      }

      .mini-cart-content {
        background: white;
        border-radius: 12px;
        padding: 24px;
        max-width: 400px;
        width: 90%;
        text-align: center;
        transform: scale(0.8);
        transition: transform 0.3s ease;
        position: relative;
        z-index: 1;
      }

      #mini-cart-popup.show .mini-cart-content {
        transform: scale(1);
      }

      .mini-cart-header h3 {
        margin: 12px 0 0 0;
        font-size: 20px;
        font-weight: 600;
        color: #222529;
      }

      .checkmark {
        width: 48px;
        height: 48px;
        fill: #27ae60;
        margin-bottom: 8px;
      }

      .mini-cart-product {
        display: flex;
        align-items: center;
        gap: 16px;
        margin: 20px 0 24px 0;
        padding: 16px;
        background: #f8f9fa;
        border-radius: 8px;
      }

      .mini-cart-product img {
        width: 60px;
        height: 60px;
        object-fit: cover;
        border-radius: 6px;
      }

      .product-details {
        text-align: left;
        flex: 1;
      }

      .product-title {
        font-size: 14px;
        font-weight: 600;
        margin: 0 0 4px 0;
        color: #222529;
      }

      .product-price {
        font-size: 16px;
        font-weight: 700;
        color: #222529;
        margin: 0 0 4px 0;
      }

      .product-variant {
        font-size: 12px;
        color: #666;
        margin: 0;
      }

      .mini-cart-actions {
        display: flex;
        gap: 12px;
      }

      .continue-shopping {
        flex: 1;
        background: #f8f9fa;
        border: 1px solid #dee2e6;
        padding: 12px;
        border-radius: 6px;
        cursor: pointer;
        font-weight: 500;
        transition: all 0.2s ease;
      }

      .continue-shopping:hover {
        background: #e9ecef;
      }

      .view-cart {
        flex: 1;
        background: #222529;
        color: white;
        padding: 12px;
        border-radius: 6px;
        text-decoration: none;
        font-weight: 600;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.2s ease;
      }

      .view-cart:hover {
        background: #333;
        color: white;
      }

      .error-toast {
        position: fixed;
        top: 20px;
        right: 20px;
        background: #dc3545;
        color: white;
        padding: 12px 20px;
        border-radius: 6px;
        z-index: 10001;
        transform: translateX(100%);
        transition: transform 0.3s ease;
        display: flex;
        align-items: center;
        gap: 8px;
      }

      .error-toast.show {
        transform: translateX(0);
      }

      /* Mobile Responsive */
      @media (max-width: 767px) {
        .sticky-cart-enhanced {
          left: 15px;
          right: 15px;
          bottom: 80px;
          border-radius: 25px;
          max-width: none;
        }

        .sticky-cart-content {
          padding: 8px 0;
        }

        .sticky-cart-btn {
          padding: 8px 16px;
          font-size: 12px;
          min-width: 120px;
        }

        .mini-cart-content {
          padding: 20px;
        }

        .mini-cart-actions {
          flex-direction: column;
        }
      }
    `;

    document.head.appendChild(styles);
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    setTimeout(init, 200);
  }

  // Expose for manual initialization
  window.EnhancedCartPopup = { init, showMiniCartPopup, hideMiniCartPopup };
})();